<template>
  <div class="areceber-workbench" :class="{ 'theme-dark': isDark }">
    <!-- Header -->
    <div class="workbench-header">
      <div>
        <b-button variant="link" class="back-btn" @click="$router.push('/movimentos/contasreceber/listagem')">
          <i class="fa fa-arrow-left"></i>
        </b-button>
        <div>
          <h3>Mesa de Trabalho do Contas a Receber</h3>
          <span>Analise, explore clientes e baixe títulos sem voltar para a listagem</span>
        </div>
      </div>
      <div class="header-actions">
        <b-form-input v-model="busca" placeholder="Buscar documento, cliente ou grupo..." style="border-radius: 999px; max-width: 220px;" />
        <b-button variant="info" pill class="text-white font-weight-bold" @click="abrirModalBaixarLote">
          <i class="fa fa-university mr-1"></i> Baixar em Lote
        </b-button>
        <b-button variant="light" pill :disabled="loading" @click="carregar">
          <i class="fa fa-refresh mr-1" :class="{ 'fa-spin': loading }"></i> Atualizar
        </b-button>
      </div>
    </div>

    <b-overlay :show="loading" rounded="sm">
      <!-- KPIs -->
      <div class="kpi-grid">
        <button v-for="card in kpis" :key="card.label" type="button" class="kpi-card" :class="[card.class, { active: isCardSelected(card) }]" @click="aplicarAtalho(card.filter)">
          <small>{{ card.label }}</small>
          <strong>{{ card.value }}</strong>
          <span>{{ card.hint }}</span>
          <i :class="card.icon"></i>
        </button>
      </div>

      <!-- Dashboard Grid -->
      <div class="workbench-grid">
        <section class="panel panel-wide">
          <div class="panel-head">
            <div>
              <strong>Clientes devedores</strong>
              <span>Clique para abrir os títulos do cliente</span>
            </div>
            <b-badge variant="info" pill>{{ fornecedores.length }}</b-badge>
          </div>
          <div class="supplier-board">
            <button v-for="f in fornecedores" :key="f.COD_FORNEC" type="button" class="supplier-card" :class="{ active: filtro.tipo === 'fornecedor' && filtro.valor === f.COD_FORNEC }" @click="filtrar('fornecedor', f.COD_FORNEC, f.NOME_FORNECEDOR)">
              <span>{{ f.COD_FORNEC }} - {{ f.NOME_FORNECEDOR }}</span>
              <strong>{{ formatBRL(f.TOTAL_ABERTO) }}</strong>
              <em>{{ f.TITULOS }} títulos | {{ formatBRL(f.TOTAL_ATRASADO) }} atraso</em>
              <b-progress :value="percent(f.TOTAL_ABERTO, maxFornecedor)" max="100" height="7px" variant="info" />
            </button>
          </div>
        </section>

        <section class="panel">
          <div class="panel-head">
            <div>
              <strong>Faixas de atraso</strong>
              <span>Clique no gráfico para filtrar</span>
            </div>
          </div>
          <div class="chart-wrap">
            <canvas ref="chartFaixas"></canvas>
          </div>
        </section>

        <section class="panel">
          <div class="panel-head">
            <div>
              <strong>Grupos de receita</strong>
              <span>Distribuição da carteira</span>
            </div>
          </div>
          <div class="chart-wrap">
            <canvas ref="chartGrupos"></canvas>
          </div>
        </section>

        <section class="panel">
          <div class="panel-head">
            <div>
              <strong>Recebido hoje</strong>
              <span>Movimento do dia</span>
            </div>
          </div>
          <div class="today-paid">
            <strong>{{ formatBRL(baixadoHoje.TOTAL) }}</strong>
            <span>{{ baixadoHoje.TITULOS || 0 }} recebimentos efetuados</span>
          </div>
          <div class="recent-list">
            <div v-for="p in ultimasBaixas.slice(0, 7)" :key="p.SEQUENCIA" class="recent-row">
              <div>
                <strong>{{ p.DOCUMENTO }} / {{ p.PARCELAS }}</strong>
                <span>{{ p.COD_FORNEC }} - {{ p.NOME_FORNECEDOR }}</span>
              </div>
              <div class="recent-row-actions">
                <em>{{ formatBRL(p.VL_PAGO) }}</em>
                <b-button
                  type="button"
                  size="sm"
                  variant="soft-danger"
                  pill
                  class="btn-icon-only shadow-sm"
                  title="Estornar Recebimento"
                  :disabled="estorno.parametros.overlayBaixando"
                  @click.stop="abrirModalEstornoRecebimento(p)"
                  v-b-tooltip.hover.left
                >
                  <i class="fa fa-reply"></i>
                </b-button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- Títulos em foco -->
      <section class="panel table-panel">
        <div class="panel-head">
          <div>
            <strong>Títulos em foco</strong>
            <span>
              {{ filtroLabel }} | {{ titulosFiltrados.length }} registros | {{ formatBRL(totalFiltrado) }}
              <span v-if="selecionados.length" class="text-success font-weight-bold ml-3 border-left pl-3">
                <i class="fa fa-check-square-o mr-1"></i> {{ selecionados.length }} selecionados | Total: {{ formatBRL(totalSelecionado) }}
              </span>
            </span>
          </div>
          <div class="d-flex align-items-center">
            <b-button v-if="filtro.tipo" variant="outline-secondary" pill size="sm" class="mr-2" @click="limparFiltro">
              <i class="fa fa-times mr-1"></i> Limpar seleção
            </b-button>
            <b-button variant="success" pill size="sm" :disabled="!selecionados.length" @click="baixarSelecionados">
              <i class="fa fa-check-circle mr-1"></i> Baixar selecionados
            </b-button>
          </div>
        </div>
        <b-table
          ref="tabela"
          small
          hover
          responsive
          selectable
          select-mode="multi"
          selected-variant="success"
          :items="titulosFiltrados"
          :fields="fields"
          :per-page="perPage"
          :current-page="currentPage"
          @row-selected="selecionados = $event"
        >
          <template #cell(DOCUMENTO)="data">
            <div class="d-flex align-items-center">
              <b-button variant="link" class="p-0 font-weight-bold text-left" @click="editar(data.item)">{{ data.item.DOCUMENTO }}</b-button>
              <b-button v-if="data.item.OP_STATUS_BOLETO && data.item.OP_STATUS_BOLETO.trim() !== '0'" variant="link" class="p-0 ml-2 text-primary" @click.stop="downloadBoleto(data.item)" v-b-tooltip.hover title="Visualizar Boleto">
                <i class="fa fa-barcode"></i>
              </b-button>
            </div>
          </template>
          <template #cell(OP_STATUS_BOLETO)="data">
            <i v-if="data.item.OP_STATUS_BOLETO && data.item.OP_STATUS_BOLETO.trim() !== '0'" :class="classIconeBoleto(data.item.OP_STATUS_BOLETO)"></i>
            <span v-else class="text-muted">-</span>
          </template>
          <template #cell(NOME_FORNECEDOR)="data">
            <div class="font-weight-bold text-dark text-left">{{ data.item.NOME_FORNECEDOR }}</div>
            <small class="text-muted d-block text-left">Cód: {{ data.item.COD_FORNEC }}</small>
          </template>
          <template #cell(DT_DOC)="data">{{ formatDate(data.item.DT_DOC) }}</template>
          <template #cell(DT_VENC)="data">{{ formatDate(data.item.DT_VENC) }}</template>
          <template #cell(DIAS_ATRASO)="data">
            <b-badge :variant="data.item.DIAS_ATRASO > 0 ? 'danger' : 'success'" pill>{{ data.item.DIAS_ATRASO || 0 }}</b-badge>
          </template>
          <template #cell(VL_DOC)="data">{{ formatBRL(data.item.VL_DOC) }}</template>
          <template #cell(VL_RESTANTE)="data">{{ formatBRL(data.item.VL_RESTANTE) }}</template>
          <template #cell(actions)="data">
            <div class="d-flex align-items-center gap-1 justify-content-end">
              <b-button type="button" @click="abrirModalBaixar(data.item)" size="sm" variant="soft-success" pill class="btn-icon-only shadow-sm mr-1" title="Baixar Titulo" v-b-tooltip.hover.left>
                <i class="fa fa-money"></i>
              </b-button>

              <b-dropdown
                  right
                  boundary="viewport"
                  :popper-opts="{ positionFixed: true }"
                  no-caret
                  variant="link"
                  toggle-class="p-0 text-muted no-decoration"
              >
                <template #button-content>
                  <b-button variant="soft-primary" pill class="btn-icon-only shadow-sm">
                    <i class="fa fa-ellipsis-v"></i>
                  </b-button>
                </template>

                <b-dropdown-item v-if="((data.item.OP_STATUS_BOLETO) && (data.item.OP_STATUS_BOLETO.trim() != '0'))" @click="downloadBoleto(data.item)"><i class="fa fa-eye text-primary"></i> Visualizar Boleto</b-dropdown-item>
                <b-dropdown-item v-else @click="abrirModalGerarBoleto(data.item)"><i class="fa fa-barcode text-info"></i> Gerar Boleto</b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item @click="cancelarBoleto(data.item)"><i class="fa fa-close text-danger"></i> Cancelar Boleto</b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </b-table>
        <b-pagination v-model="currentPage" :total-rows="titulosFiltrados.length" :per-page="perPage" align="right" class="mb-0" />
      </section>
    </b-overlay>

    <!--  -----------------------------------------------------------------------------------
                                      MODAL RECEBIMENTOS (BAIXA INDIVIDUAL)
          ----------------------------------------------------------------------------------- -->
    <b-modal ref="modal-baixar" 
        id="modal-baixar" 
        hide-header
        hide-footer
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg"
        centered 
        size="lg"
        :no-close-on-backdrop="overlayBaixando"
        :no-close-on-esc="overlayBaixando"
        :hide-header-close="overlayBaixando"
        @shown="focusModalReceber()" 
    >
      <b-overlay :show="overlayBaixando" 
                  no-wrap 
                  fixed 
                  z-index="1100" 
                  spinner-variant="primary"
                  spinner-type="border">
        <template #overlay>
          <div class="text-center bg-white p-4 rounded shadow-lg border">
            <b-icon icon="stopwatch" font-scale="3" class="text-primary mb-3" animation="cylon"></b-icon>
            <h5 class="font-weight-bold">Processando Recebimento...</h5>
            <p class="text-muted mb-0">Por favor, aguarde um momento.</p>
          </div>
        </template>
      </b-overlay>

      <div class="modal-header-premium p-3 d-flex align-items-center bg-dark" style="background: linear-gradient(135deg, #20a8d8 0%, #1783ae 100%) !important;">
          <div class="header-icon-wrapper mr-3 bg-white-10">
              <i class="fa fa-money text-white"></i>
          </div>
          <div>
              <h5 class="mb-0 font-weight-bold text-white">Baixar Título a Receber</h5>
              <small class="text-white opacity-75">Confirmação de entrada financeira no caixa/banco</small>
          </div>
          <b-button variant="link" class="ml-auto text-white p-0 opacity-75" :disabled="overlayBaixando" @click="$bvModal.hide('modal-baixar')">
              <i class="fa fa-times fa-lg"></i>
          </b-button>
      </div>

      <div class="p-4 bg-white">
          <!-- Modal Info Bar -->
          <div class="modal-info-bar mb-4 bg-light border shadow-sm p-3 rounded">
              <b-row align-v="center">
                  <b-col md="6">
                      <small class="modal-label-premium">Cliente Pagador</small>
                      <h5 class="mb-0 font-weight-bold text-dark">{{recebido.nomeCliente}}</h5>
                  </b-col>
                  <b-col md="3" class="border-left">
                      <small class="modal-label-premium">Documento / Venc.</small>
                      <h5 class="mb-0 font-weight-bold text-primary">{{recebido.documento}}</h5>
                      <small class="text-muted font-weight-bold">{{recebido.dataVencimento}}</small>
                  </b-col>
                  <b-col md="3" class="border-left text-right">
                      <small class="modal-label-premium">Valor Original</small>
                      <h5 class="mb-0 font-weight-bold text-success">{{formatCurrency(recebido.valorDocumento)}}</h5>
                  </b-col>
              </b-row>
          </div>

          <div v-if="modalRecebimentos.parametros.permissao === true">
              <div class="modal-section-title mb-3">
                  <i class="fa fa-cogs mr-2 text-primary"></i> Detalhes do Recebimento
              </div>
              
              <div class="p-4 bg-light rounded border shadow-xs mb-4">
                  <b-row class="mb-3">
                      <b-col md="6">
                          <label class="modal-label-premium">Tipo de Recebimento</label>
                          <b-form-select id="tiporecebimento" ref="tiporecebimento"
                              v-model="recebido.tipoRecebimento"
                              :options="tipoRecebimentoOpcoes"
                              class="rounded-pill shadow-xs"
                          ></b-form-select>
                      </b-col>
                      <b-col md="6">
                          <label class="modal-label-premium">Data do Recebimento</label>
                          <b-form-input type="date" 
                              id="dataBaixa" 
                              v-model="recebido.dataMovimento"
                              class="rounded-pill shadow-xs"
                          ></b-form-input>
                      </b-col>
                  </b-row>

                  <b-row class="mb-3">
                      <b-col md="6">
                          <label class="modal-label-premium">Valor Recebido R$</label>
                          <div class="d-flex gap-2">
                              <vue-autonumeric
                                  id="valorRecebido" 
                                  ref="valorRecebido"
                                  v-model="recebido.valorRecebido"
                                  :options="'Brazilian'"
                                  :class="['form-control rounded-pill shadow-xs', { 'is-invalid': !validouCamposGeralBaixa }]" 
                                  style="text-align: right"
                              ></vue-autonumeric>
                              <b-button variant="soft-primary" pill class="px-3 font-weight-bold ml-1" @click="usarRestante()" title="Usar valor restante">
                                  <i class="fa fa-magic"></i>
                              </b-button>
                          </div>
                      </b-col>
                      <b-col md="6">
                        <label class="modal-label-premium">Ajustes R$ (Informativo)</label>
                        <vue-autonumeric  
                          id="valorAjustes" 
                          v-model="recebido.valorAjustes" 
                          :options="'Brazilian'" 
                          class="form-control rounded-pill shadow-xs bg-light" 
                          style="text-align: right"
                          :disabled="true"
                        ></vue-autonumeric>
                      </b-col>
                  </b-row>

                  <b-row>
                      <b-col md="12">
                          <label class="modal-label-premium">Conta de Destino</label>
                          <b-form-select   
                              v-model="recebido.contaId" 
                              :options="contasOpcoes" 
                              :state="!!recebido.contaId"
                              ref="comboconta"
                              class="rounded-pill shadow-xs"
                          >
                              <template slot="first">
                                  <option :value="null">-- Selecione a conta bancária --</option>
                              </template>
                          </b-form-select>
                      </b-col>
                  </b-row>

                  <!-- Campos específicos para PIX -->
                  <div v-if="recebido.tipoRecebimento === '6'" class="mt-3">
                      <div class="modal-section-title mb-3">
                          <i class="fa fa-qrcode mr-2 text-primary"></i> Dados do PIX
                      </div>
                      
                      <b-row class="mb-3">
                          <b-col md="6">
                              <label class="modal-label-premium">Tipo de Chave PIX <span class="text-danger">*</span></label>
                              <b-form-select
                                  v-model="recebido.tipoChavePix"
                                  :options="tipoChavePixOpcoes"
                                  :state="!!recebido.tipoChavePix"
                                  class="rounded-pill shadow-xs"
                              >
                                  <template slot="first">
                                      <option :value="null">-- Selecione o tipo de chave --</option>
                                  </template>
                              </b-form-select>
                          </b-col>
                          <b-col md="6">
                              <label class="modal-label-premium">Nome do Pagador</label>
                              <b-form-input
                                  v-model="recebido.nomePagadorPix"
                                  placeholder="Nome completo do pagador"
                                  class="rounded-pill shadow-xs"
                                  maxlength="100"
                              ></b-form-input>
                          </b-col>
                      </b-row>

                      <b-row class="mb-3">
                          <b-col md="12">
                              <label class="modal-label-premium">Banco de Origem</label>
                              <b-form-select
                                  v-model="recebido.bancoPixId"
                                  :options="bancosPixOpcoes"
                                  class="rounded-pill shadow-xs"
                              >
                                  <template slot="first">
                                      <option :value="null">-- Selecione o banco de origem --</option>
                                  </template>
                              </b-form-select>
                          </b-col>
                      </b-row>
                  </div>
              </div>

              <div class="modal-section-title mb-3">
                  <i class="fa fa-money mr-2 text-primary"></i> Resumo de Saldos
              </div>
              
              <b-row>
                <b-col md="3">
                      <div class="p-3 bg-white rounded border shadow-xs text-center h-100">
                          <small class="modal-label-premium">Vlr. Original</small>
                          <h5 class="mb-0 font-weight-bold text-dark">{{formatCurrency(recebido.valorDocumento)}}</h5>
                      </div>
                  </b-col>
                <b-col md="3">
                      <div class="p-3 bg-white rounded border shadow-xs text-center h-100">
                          <small class="modal-label-premium">Já Recebido</small>
                          <h5 class="mb-0 font-weight-bold text-info">{{formatCurrency(recebido.valorRecebimentos)}}</h5>
                      </div>
                  </b-col>
                <b-col md="3">
                  <div class="p-3 bg-white rounded border shadow-xs text-center h-100">
                    <small class="modal-label-premium">Ajustes</small>
                    <h5 class="mb-0 font-weight-bold text-warning">{{formatCurrency(recebido.valorAjustes)}}</h5>
                  </div>
                </b-col>
                <b-col md="3">
                      <div class="p-3 bg-white rounded border shadow-sm text-center h-100 border-success border-2">
                          <small class="modal-label-premium text-success">Vlr. Restante</small>
                          <h4 class="mb-0 font-weight-bold text-success">{{formatCurrency(recebido.valorRestante)}}</h4>
                      </div>
                  </b-col>
              </b-row>
          </div>

          <div v-else class="alert bg-soft-danger border-danger text-danger text-center p-4 rounded-lg shadow-xs">
              <i class="fa fa-lock fa-3x mb-3"></i>
              <h4 class="font-weight-bold mb-1">ACESSO NEGADO</h4>
              <p class="mb-0">Você não possui permissões suficientes para realizar a baixa deste título.</p>
          </div>
      </div>

      <div class="modal-header-premium p-3 bg-light border-top d-flex justify-content-between">
          <b-button variant="soft-danger" pill class="px-4 font-weight-bold" :disabled="overlayBaixando" @click="$bvModal.hide('modal-baixar')">
              CANCELAR
          </b-button>
          <b-button v-if="modalRecebimentos.parametros.permissao === true" variant="success" pill class="px-5 font-weight-bold shadow-sm" :disabled="overlayBaixando" @click="baixar()">
              <i class="fa mr-1" :class="overlayBaixando ? 'fa-spinner fa-spin' : 'fa-check-circle'"></i>
              {{ overlayBaixando ? 'PROCESSANDO...' : 'CONFIRMAR RECEBIMENTO' }}
          </b-button>
      </div>
    </b-modal>

    <!--  -----------------------------------------------------------------------------------
                               MODAL BAIXAR EM LOTE (ESPECIFICO ANALYTICS)
          ----------------------------------------------------------------------------------- -->
    <b-modal ref="modal-baixar-lote" 
          id="modal-baixar-lote" 
          size="lg"
          hide-header
          hide-footer
          body-class="p-0 overflow-hidden"
          content-class="border-0 shadow-lg"
          centered 
          @shown="focusModalBaixarLote()" 
          title="Baixar em Lote" >
      <b-overlay :show="lote.overlay" 
                  no-wrap 
                  fixed 
                  z-index="1100" 
                  spinner-variant="primary"
                  spinner-type="border">
        <template #overlay>
          <div class="text-center bg-white p-4 rounded shadow-lg border">
            <b-icon icon="stopwatch" font-scale="3" class="text-primary mb-3" animation="cylon"></b-icon>
            <h5 class="font-weight-bold">Processando Baixa em Lote...</h5>
            <p class="text-muted mb-0">Por favor, aguarde um momento.</p>
          </div>
        </template>
      </b-overlay>

      <div class="modal-header-premium p-3 d-flex align-items-center bg-dark" style="background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;">
          <div class="header-icon-wrapper mr-3 bg-white-10">
              <i class="fa fa-university text-white"></i>
          </div>
          <div>
              <h5 class="mb-0 font-weight-bold text-white">Baixar Títulos em Lote</h5>
              <small class="text-white opacity-75">Confirmação de recebimento para múltiplos títulos</small>
          </div>
          <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="$refs['modal-baixar-lote'].hide()">
              <i class="fa fa-times fa-lg"></i>
          </b-button>
      </div>

      <div class="p-4 bg-white">
          <!-- Info Bar -->
          <div class="modal-info-bar mb-4 bg-light border shadow-sm p-3 rounded">
              <b-row align-v="center">
                  <b-col md="8">
                      <small class="modal-label-premium">Cliente Pagador</small>
                      <h5 class="mb-0 font-weight-bold text-dark">{{ lote.nomeCliente }}</h5>
                      <small class="text-muted font-weight-bold">{{ lote.titulos.length }} títulos selecionados na mesa</small>
                  </b-col>
                  <b-col md="4" class="border-left text-right">
                      <small class="modal-label-premium">Valor Total a Baixar</small>
                      <h4 class="mb-0 font-weight-bold text-success">{{ formatBRL(lote.valorTotalRecebido) }}</h4>
                  </b-col>
              </b-row>
          </div>

          <div class="modal-section-title mb-3">
              <i class="fa fa-cogs mr-2 text-primary"></i> Detalhes da Baixa
          </div>

          <div class="p-4 bg-light rounded border shadow-xs mb-4">
              <b-row class="mb-3">
                  <b-col md="6">
                      <label class="modal-label-premium">Tipo de Recebimento</label>
                      <b-form-select v-model="lote.tipoRecebimento" :options="tipoRecebimentoOpcoes" class="rounded-pill shadow-xs"></b-form-select>
                  </b-col>
                  <b-col md="6">
                      <label class="modal-label-premium">Data Movimento</label>
                      <b-form-input type="date" v-model="lote.dataMovimento" class="rounded-pill shadow-xs"></b-form-input>
                  </b-col>
              </b-row>
              <b-row>
                  <b-col md="12">
                      <label class="modal-label-premium">Conta de Destino</label>
                      <b-form-select   
                          v-model="lote.contaId" 
                          :options="contasOpcoes" 
                          :state="!!lote.contaId"
                          class="rounded-pill shadow-xs"
                      >
                          <template slot="first">
                              <option :value="null">-- Selecione a conta de destino --</option>
                          </template>
                      </b-form-select>
                  </b-col>
              </b-row>

              <!-- Campos específicos para PIX (Lote) -->
              <div v-if="lote.tipoRecebimento === '6'" class="mt-3">
                  <div class="modal-section-title mb-3">
                      <i class="fa fa-qrcode mr-2 text-primary"></i> Dados do PIX
                  </div>
                  
                  <b-row class="mb-3">
                      <b-col md="6">
                          <label class="modal-label-premium">Tipo de Chave PIX <span class="text-danger">*</span></label>
                          <b-form-select
                              v-model="lote.tipoChavePix"
                              :options="tipoChavePixOpcoes"
                              :state="!!lote.tipoChavePix"
                              class="rounded-pill shadow-xs"
                          >
                              <template slot="first">
                                  <option :value="null">-- Selecione o tipo de chave --</option>
                              </template>
                          </b-form-select>
                      </b-col>
                      <b-col md="6">
                          <label class="modal-label-premium">Nome do Pagador</label>
                          <b-form-input
                              v-model="lote.nomePagadorPix"
                              placeholder="Nome completo do pagador"
                              class="rounded-pill shadow-xs"
                              maxlength="100"
                          ></b-form-input>
                      </b-col>
                  </b-row>

                  <b-row>
                      <b-col md="12">
                          <label class="modal-label-premium">Banco de Origem</label>
                          <b-form-select
                              v-model="lote.bancoPixId"
                              :options="bancosPixOpcoes"
                              class="rounded-pill shadow-xs"
                          >
                              <template slot="first">
                                  <option :value="null">-- Selecione o banco de origem --</option>
                              </template>
                          </b-form-select>
                      </b-col>
                  </b-row>
              </div>
          </div>

          <div class="modal-section-title mb-3">
              <i class="fa fa-list mr-2 text-primary"></i> Títulos do Lote
          </div>

          <div class="border rounded overflow-hidden" style="max-height: 200px; overflow-y: auto;">
              <b-table 
                  small 
                  striped 
                  hover 
                  :items="lote.titulos" 
                  :fields="loteFields" 
                  class="mb-0 table-finance"
                  primary-key="SEQUENCIA"
                  :dark="isDark"
                  show-empty
                  empty-text="Nenhum título selecionado.">
                  
                  <template #cell(DT_VENC)="row">
                      {{ formatDate(row.item.DT_VENC) }}
                  </template>

                  <template #cell(VL_RESTANTE)="row">
                       {{ formatBRL(row.item.VL_RESTANTE) }}
                  </template>
              </b-table>
          </div>
      </div>

      <div class="modal-header-premium p-3 bg-light border-top d-flex justify-content-between">
          <b-button variant="soft-danger" pill class="px-4 font-weight-bold" @click="$refs['modal-baixar-lote'].hide()">
              CANCELAR
          </b-button>
          <b-button variant="success" pill class="px-5 font-weight-bold shadow-sm" :disabled="!lote.contaId || lote.valorTotalRecebido <= 0" @click="confirmarBaixaLote()">
              <i class="fa fa-check-circle mr-1"></i> CONFIRMAR BAIXA EM LOTE
          </b-button>
      </div>
    </b-modal>

    <!--  -----------------------------------------------------------------------------------
                                MODAL ESTORNO RECEBIMENTO
          ----------------------------------------------------------------------------------- -->
    <b-modal ref="modal-estorno" 
        id="modal-estorno" 
        hide-header
        hide-footer
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg"
        centered
        size="lg"
        :no-close-on-backdrop="estorno.parametros.overlayBaixando"
        :no-close-on-esc="estorno.parametros.overlayBaixando"
        :hide-header-close="estorno.parametros.overlayBaixando"
        @shown="focusModalEstornar()"
    >
      <b-overlay :show="estorno.parametros.overlayBaixando" 
                  no-wrap 
                  fixed 
                  z-index="1100" 
                  spinner-variant="danger"
                  spinner-type="border">
        <template #overlay>
          <div class="text-center bg-white p-4 rounded shadow-lg border">
            <b-icon icon="slash-circle" font-scale="3" class="text-danger mb-3" animation="pulse"></b-icon>
            <h5 class="font-weight-bold">Processando Estorno...</h5>
            <p class="text-muted mb-0">Por favor, aguarde um momento.</p>
          </div>
        </template>
      </b-overlay>

      <div class="modal-header-premium p-3 d-flex align-items-center bg-dark" style="background: linear-gradient(135deg, #e53e3e 0%, #7d1c1c 100%) !important;">
          <div class="header-icon-wrapper mr-3 bg-white-10">
              <i class="fa fa-reply text-white"></i>
          </div>
          <div>
              <h5 class="mb-0 font-weight-bold text-white">Estornar Recebimento</h5>
              <small class="text-white opacity-75">Anulação de entrada financeira no caixa/banco</small>
          </div>
          <b-button variant="link" class="ml-auto text-white p-0 opacity-75" :disabled="estorno.parametros.overlayBaixando" @click="$bvModal.hide('modal-estorno')">
              <i class="fa fa-times fa-lg"></i>
          </b-button>
      </div>

      <div class="p-4 bg-white">
          <div class="modal-info-bar mb-4 bg-light border shadow-sm p-3 rounded">
              <b-row align-v="center">
                  <b-col md="6">
                      <small class="modal-label-premium">Cliente</small>
                      <h5 class="mb-0 font-weight-bold text-dark">{{ estorno.dados.nomeCliente }}</h5>
                  </b-col>
                  <b-col md="3" class="border-left">
                      <small class="modal-label-premium">Documento / Parcela</small>
                      <h5 class="mb-0 font-weight-bold text-primary">{{ estorno.dados.documento }} - {{ estorno.dados.parcela }}</h5>
                      <small class="text-muted font-weight-bold">Pago em: {{ estorno.dados.dataRecebimento }}</small>
                  </b-col>
                  <b-col md="3" class="border-left text-right">
                      <small class="modal-label-premium">Valor Recebido</small>
                      <h5 class="mb-0 font-weight-bold text-danger">{{ formatCurrency(estorno.dados.valorRecebido) }}</h5>
                  </b-col>
              </b-row>
          </div>

          <div class="modal-section-title mb-3">
              <i class="fa fa-commenting mr-2 text-danger"></i> Justificativa do Estorno
          </div>
          <div class="p-3 bg-light rounded border mb-3 shadow-xs">
               <label class="modal-label-premium">Motivo do Estorno <span class="text-danger">*</span></label>
               <b-form-input
                  ref="motivoEstorno"
                  id="motivoEstorno"
                  v-model="estorno.dados.motivo"
                  :state="validouCampoMotivoEstorno"
                  placeholder="Informe detalhadamente o porquê deste estorno..."
                  class="rounded-pill shadow-xs"
                  :disabled="estorno.parametros.overlayBaixando"
                  @keydown.enter.native="estornarRecebimento()"
               ></b-form-input>
               <small class="text-muted mt-2 d-block"><i class="fa fa-info-circle mr-1"></i> Esta ação irá reabrir o título no Contas a Receber.</small>
          </div>

          <div class="modal-section-title mb-3">
              <i class="fa fa-money mr-2 text-primary"></i> Resumo de Valores
          </div>
          
          <b-row>
              <b-col md="6">
                  <div class="p-3 bg-white rounded border shadow-xs text-center h-100">
                      <small class="modal-label-premium">Valor do Documento</small>
                      <h5 class="mb-0 font-weight-bold text-dark">{{ formatCurrency(estorno.dados.valorDocumento) }}</h5>
                  </div>
              </b-col>
              <b-col md="6">
                  <div class="p-3 bg-white rounded border shadow-sm text-center h-100 border-danger border-2">
                      <small class="modal-label-premium text-danger">Valor a Estornar</small>
                      <h4 class="mb-0 font-weight-bold text-danger">{{ formatCurrency(estorno.dados.valorRecebido) }}</h4>
                  </div>
              </b-col>
          </b-row>
      </div>

      <div class="modal-footer-premium p-3 bg-light border-top d-flex justify-content-between">
          <b-button variant="soft-danger" pill class="px-4 font-weight-bold" :disabled="estorno.parametros.overlayBaixando" @click="$bvModal.hide('modal-estorno')">
              CANCELAR
          </b-button>
          <b-button variant="danger" pill class="px-5 font-weight-bold shadow-sm" :disabled="estorno.parametros.overlayBaixando" @click="estornarRecebimento()">
              <i class="fa mr-1" :class="estorno.parametros.overlayBaixando ? 'fa-spinner fa-spin' : 'fa-check-circle'"></i>
              {{ estorno.parametros.overlayBaixando ? 'PROCESSANDO...' : 'CONFIRMAR ESTORNO' }}
          </b-button>
      </div>
    </b-modal>

    <!--  -----------------------------------------------------------------------------------
                                      MODAL GERAR BOLETO
          ----------------------------------------------------------------------------------- -->
    <b-modal ref="modal-boleto-gerar" 
        id="modal-boleto-gerar" 
        hide-header
        hide-footer
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg"
        centered 
        size="lg"
    >
      <b-overlay :show="overlayBaixando" 
                  no-wrap 
                  fixed 
                  z-index="1100" 
                  spinner-variant="primary"
                  spinner-type="border">
        <template #overlay>
          <div class="text-center bg-white p-4 rounded shadow-lg border">
            <b-icon icon="stopwatch" font-scale="3" class="text-primary mb-3" animation="cylon"></b-icon>
            <h5 class="font-weight-bold">Gerando Boleto...</h5>
            <p class="text-muted mb-0">Comunicação bancária em andamento.</p>
          </div>
        </template>
      </b-overlay>
      
      <!-- Header Premium -->
      <div class="modal-header-premium p-3 d-flex align-items-center bg-dark" style="background: linear-gradient(135deg, #2c3e50 0%, #000000 100%) !important;">
          <div class="header-icon-wrapper mr-3 bg-white-10">
              <i class="fa fa-barcode text-white"></i>
          </div>
          <div>
              <h5 class="mb-0 font-weight-bold text-white">Gerar Título Bancário</h5>
              <small class="text-white opacity-75">Emissão de boleto para cobrança</small>
          </div>
          <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="$bvModal.hide('modal-boleto-gerar')">
              <i class="fa fa-times fa-lg"></i>
          </b-button>
      </div>

      <div class="p-4 bg-white">
           <!-- Info Bar -->
          <div class="modal-info-bar mb-4 bg-light border shadow-sm p-3 rounded">
              <b-row align-v="center">
                  <b-col md="8">
                      <small class="modal-label-premium">Cliente Pagador</small>
                      <h5 class="mb-0 font-weight-bold text-dark">{{ boleto.clienteId }} - {{ boleto.nomeCliente }}</h5>
                      <small class="text-muted font-weight-bold">Título: {{ boleto.documento }} / {{ boleto.parcela }}</small>
                  </b-col>
                  <b-col md="3" class="border-left text-right">
                      <small class="modal-label-premium text-right">Valor Total</small>
                      <h5 class="mb-0 font-weight-bold text-success">{{formatCurrency(boleto.valorDocumento)}}</h5>
                  </b-col>
              </b-row>
          </div>

          <div class="modal-section-title mb-3">
              <i class="fa fa-university mr-2 text-primary"></i> Configurações de Recebimento
          </div>
          
          <div class="p-4 bg-light rounded border shadow-xs mb-4">
              <b-row class="mb-3">
                <b-col md="12">
                  <label class="modal-label-premium">Empresa Emitente</label>
                  <b-form-select 
                      v-model="boleto.empresaId"
                      :options="empresas.options" 
                      :state="!!boleto.empresaId"
                      class="rounded-pill shadow-xs"
                  />
                </b-col> 
              </b-row>    
              <b-row >
                <b-col md="12">
                   <label class="modal-label-premium">Conta para Depósito / Cobrança</label>
                    <b-form-select   
                                    v-model="boleto.contaId" 
                                    :options="contasOpcoes" 
                                    ref="comboconta"
                                    class="rounded-pill shadow-xs"
                                    >
                      <template slot="first">
                        <option :value="null" >-- Selecione a conta bancária --</option>
                      </template>
                    </b-form-select>
                </b-col>
              </b-row>
          </div>
          
           <div class="modal-section-title mb-3">
              <i class="fa fa-money mr-2 text-primary"></i> Resumo Financeiro
          </div>
          
          <b-row class="mb-0">
            <b-col sm="4">
              <div class="p-3 bg-white rounded border shadow-xs text-center h-100">
                  <small class="modal-label-premium">Vlr. Original</small>
                  <vue-autonumeric id="valorOriginal" v-model="boleto.valorDocumento" :options="'Brazilian'" class="form-control border-0 text-center font-weight-bold text-dark bg-transparent" style="font-size: 1.1rem;" :disabled="true"></vue-autonumeric>
              </div>
            </b-col>
            <b-col sm="4">
               <div class="p-3 bg-white rounded border shadow-xs text-center h-100">
                  <small class="modal-label-premium">Recebimentos</small>
                  <vue-autonumeric id="valorRecebimentos" v-model="boleto.valorRecebimentos" :options="'Brazilian'" class="form-control border-0 text-center font-weight-bold text-info bg-transparent" style="font-size: 1.1rem;" :disabled="true"></vue-autonumeric>
               </div>
            </b-col>
            <b-col sm="4">
               <div class="p-3 bg-white rounded border shadow-sm text-center h-100 border-success border-2">
                   <small class="modal-label-premium text-success">Vlr. à Gerar</small>
                  <vue-autonumeric id="valorrestante" v-model="boleto.valorRestante" :options="'Brazilian'" class="form-control border-0 text-center font-weight-bold text-success bg-transparent" style="font-size: 1.2rem;" :disabled="true"></vue-autonumeric>
               </div>
            </b-col>
          </b-row>
      </div>

      <div class="modal-header-premium p-3 bg-light border-top d-flex justify-content-between">
          <b-button variant="soft-danger" pill class="px-4 font-weight-bold" @click="$bvModal.hide('modal-boleto-gerar')">
              CANCELAR
          </b-button>
          <b-button variant="success" pill class="px-5 font-weight-bold shadow-sm" @click="gerarBoleto()">
              <i class="fa fa-barcode mr-1"></i> GERAR BOLETO AGORA
          </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Chart from 'chart.js'
import moment from 'moment'
import VueAutonumeric from 'vue-autonumeric'
import Tools from '@/tools/tools.js'
import CecInputData from '@/components/Cec/CecInputData.vue'
import ContaDropDown from '@/views/componentes/dropdown/ContaDropDown.vue'

export default {
  name: 'AreceberAnalyticsView',
  components: { VueAutonumeric, ContaDropDown, CecInputData },
  props: {
    isDark: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      baixando: false,
      busca: '',
      filtro: { tipo: null, valor: null, label: 'Todos os títulos em aberto' },
      dados: { resumo: {}, fornecedores: [], vencimentoBuckets: [], grupos: [], titulos: [], baixadoHoje: {}, ultimasBaixas: [] },
      charts: { faixas: null, grupos: null },
      selecionados: [],
      perPage: 25,
      currentPage: 1,
      
      // Replicated data elements
      overlayBaixando: false,
      modalRecebimentos: {
        parametros: {
          permissao: false
        }
      },
      recebido: {
        areceberId: null,
        documento: null,
        parcela: null,
        clienteId: null,
        nomeCliente: null,
        dataVencimento: "00/00/0000",
        dataMovimento: "00/00/0000",
        tipoRecebimento: '0',
        valorRecebido: 0.00,
        valorAjustes: 0.00,
        valorDocumento: 0.00,
        valorRestante: 0.00,
        valorAberto: 0.00,
        valorRecebimentos: 0.00,
        contaId: null,
        vendedorId: null,
        baixaDireto: 'S',
        usuario: window.localStorage.getItem('login'),
        tipoChavePix: null,
        nomePagadorPix: null,
        bancoPixId: null
      },
      lote: {
        overlay: false,
        clienteId: null,
        nomeCliente: '',
        dataMovimento: moment().format('YYYY-MM-DD'),
        contaId: null,
        tipoRecebimento: '0',
        titulos: [],
        valorTotalRecebido: 0.00,
        todosSelecionados: false,
        tipoChavePix: null,
        nomePagadorPix: null,
        bancoPixId: null
      },
      estorno: {
        parametros: {
          overlayBaixando: false,
          permissao: false,
          processando: false
        },
        dados: {
          recebidoId: null,
          nomeCliente: '',
          documento: '',
          parcela: '',
          motivo: '',
          dataRecebimento: '',
          valorRecebido: 0.00,
          valorDocumento: 0.00,
          usuario: ''
        }
      },
      loteFields: [
        { key: 'DOCUMENTO', label: 'Documento', sortable: true },
        { key: 'PARCELAS', label: 'Parc.', sortable: true },
        { key: 'DT_VENC', label: 'Vencimento', sortable: true },
        { key: 'NOME_GRUPO', label: 'Grupo' },
        { key: 'VL_RESTANTE', label: 'Valor Aberto', class: 'text-right', sortable: true }
      ],
      boleto: {
        empresaId: null,
        contaId: null,
        areceberId: null,
        boletoId: null,
        documento: null,
        parcela: null,
        nomeCliente: null,
        clienteId: null,
        valorRecebido: 0.00,
        valorMulta: 0.00,
        valorDesconto: 0.00,
        valorDocumento: 0.00,
        valorRestante: 0.00,
        valorAberto: 0.00
      },
      empresas: {
        id: null,
        items: [],
        options: [
          { value: null, text: '' }
        ]
      },
      contasOpcoes: [],
      bancosPixOpcoes: [],
      tipoRecebimentoOpcoes: [
        { value: '0', text: 'Dinheiro/Carteira' },
        { value: '1', text: 'Deposito Bancario' },
        { value: '2', text: 'Boleto' },
        { value: '3', text: 'Cheque' },
        { value: '4', text: 'Cartao de Credito' },
        { value: '6', text: 'PIX' }
      ],
      tipoChavePixOpcoes: [
        { value: '0', text: 'CPF/CNPJ' },
        { value: '1', text: 'Celular' },
        { value: '2', text: 'Email' },
        { value: '3', text: 'Aleatória' }
      ],
      fields: [
        { key: 'DOCUMENTO', label: 'Documento', sortable: true },
        { key: 'PARCELAS', label: 'Parc.', class: 'text-center', sortable: true },
        { key: 'OP_STATUS_BOLETO', label: 'Boleto', sortable: true },
        { key: 'NOME_FORNECEDOR', label: 'Cliente', sortable: true },
        { key: 'DT_DOC', label: 'Docto.', class: 'text-right', sortable: true },
        { key: 'DT_VENC', label: 'Vencimento', class: 'text-right', sortable: true },
        { key: 'DIAS_ATRASO', label: 'Atraso', class: 'text-center', sortable: true },
        { key: 'VL_DOC', class: 'text-right', label: 'Original (R$)', sortable: true },
        { key: 'VL_RESTANTE', class: 'text-right', label: 'Restante (R$)', sortable: true },
        { key: 'actions', label: 'Ações', class: 'text-right' }
      ]
    }
  },
  computed: {
    resumo() { return this.dados.resumo || {} },
    fornecedores() { return this.dados.fornecedores || [] },
    grupos() { return this.dados.grupos || [] },
    baixadoHoje() { return this.dados.baixadoHoje || {} },
    ultimasBaixas() { return this.dados.ultimasBaixas || [] },
    maxFornecedor() { return Math.max(...this.fornecedores.map(i => Number(i.TOTAL_ABERTO) || 0), 1) },
    kpis() {
      return [
        { label: 'Total aberto', value: this.formatBRL(this.resumo.TOTAL_ABERTO), hint: `${this.resumo.TITULOS || 0} títulos`, icon: 'fa fa-briefcase', class: 'blue', filter: null },
        { label: 'Atrasado', value: this.formatBRL(this.resumo.TOTAL_ATRASADO), hint: 'clique para ver vencidos', icon: 'fa fa-warning', class: 'red', filter: { tipo: 'status', valor: 'atrasado', label: 'Títulos atrasados' } },
        { label: 'Vence hoje', value: this.formatBRL(this.resumo.VENCE_HOJE), hint: 'prioridade do dia', icon: 'fa fa-calendar-check-o', class: 'amber', filter: { tipo: 'status', valor: 'hoje', label: 'Vencendo hoje' } },
        { label: 'Próx. 7 dias', value: this.formatBRL(this.resumo.PROX_7), hint: 'pressão imediata', icon: 'fa fa-calendar', class: 'green', filter: { tipo: 'status', valor: '7dias', label: 'Vencimento nos próximos 7 dias' } },
        { label: 'Recebido hoje', value: this.formatBRL(this.baixadoHoje.TOTAL), hint: `${this.baixadoHoje.TITULOS || 0} recebimentos`, icon: 'fa fa-check-circle', class: 'teal', filter: null }
      ]
    },
    titulosFiltrados() {
      const termo = String(this.busca || '').toLowerCase()
      return (this.dados.titulos || []).filter(t => {
        const porFiltro = this.matchFiltro(t)
        const porBusca = !termo || [t.DOCUMENTO, t.PARCELAS, t.NOME_FORNECEDOR, t.NOME_GRUPO].some(v => String(v || '').toLowerCase().includes(termo))
        return porFiltro && porBusca
      })
    },
    totalFiltrado() {
      return this.titulosFiltrados.reduce((s, t) => s + (Number(t.VL_RESTANTE) || 0), 0)
    },
    totalSelecionado() {
      return this.selecionados.reduce((s, t) => s + (Number(t.VL_RESTANTE) || 0), 0)
    },
    filtroLabel() {
      return this.filtro.label || 'Todos os títulos em aberto'
    },
    
    // Replicated computed validation properties
    validouCamposGeralBaixa() {
      let retorno = true
      if (this.recebido.valorRecebido === null || Number(this.recebido.valorRecebido) <= 0.00 ||
          Tools.arredondarValor(this.recebido.valorRestante) < 0.00 ||
          Tools.arredondarValor(this.recebido.valorRecebimentos) < 0.00 ||
          !this.recebido.contaId) { retorno = false }
      return retorno
    },
    validouCampoMotivoMulta() {
      return true
    },
    validouCampoMotivoDesconto() {
      return true
    },
    validouCampoMotivoEstorno() {
      if (!this.estorno.dados.motivo) {
        return null
      }
      return this.estorno.dados.motivo.trim() !== ''
    }
  },
  watch: {
    'recebido.valorRecebido': function () {
      if (this.recebido.valorRecebido > this.recebido.valorAberto) {
        this.recebido.valorRecebido = this.recebido.valorAberto
      }
      this.calcularRecebimento()
    },
    'recebido.valorAjustes': function () {
      this.calcularRecebimento()
    }
  },
  mounted() {
    this.carregar()
  },
  beforeDestroy() {
    this.destruirCharts()
  },
  methods: {
    formatCurrency(value) {
      if (value === null || value === undefined) return 'R$ 0,00';
      const numericValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
      if (isNaN(numericValue)) return 'R$ 0,00';
      return numericValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    async carregar() {
      this.loading = true
      try {
        const { data } = await this.$http.get('/api/protected/areceber/analytics')
        this.dados = data || this.dados
        await this.carregarBancosPix()
        this.loading = false
        this.$nextTick(this.renderCharts)
      } catch (err) {
        this.loading = false
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
      }
    },
    async carregarBancosPix() {
      this.bancosPixOpcoes = []
      try {
        const { data } = await this.$http.get('/api/protected/banco-febraban/listar')
        if (data) {
          for (const banco of data) {
            this.bancosPixOpcoes.push({ value: banco.ID, text: `${banco.ID} - ${banco.NOME}` })
          }
        }
      } catch (err) {
        console.error(err)
      }
    },
    renderCharts() {
      this.destruirCharts()
      const buckets = this.dados.vencimentoBuckets || []
      this.charts.faixas = new Chart(this.$refs.chartFaixas.getContext('2d'), {
        type: 'bar',
        data: { labels: buckets.map(i => i.FAIXA), datasets: [{ data: buckets.map(i => Number(i.TOTAL) || 0), backgroundColor: ['#10b981', '#f59e0b', '#fb923c', '#ef4444', '#b91c1c', '#7f1d1d'] }] },
        options: { responsive: true, maintainAspectRatio: false, legend: { display: false }, onClick: (e) => this.chartClick(e, 'faixa'), scales: { yAxes: [{ ticks: { beginAtZero: true, callback: v => this.formatCompact(v) } }] } }
      })
      const grupos = this.grupos.slice(0, 8)
      this.charts.grupos = new Chart(this.$refs.chartGrupos.getContext('2d'), {
        type: 'doughnut',
        data: { labels: grupos.map(i => i.NOME_GRUPO), datasets: [{ data: grupos.map(i => Number(i.TOTAL_ABERTO) || 0), backgroundColor: ['#2563eb', '#0f766e', '#f59e0b', '#ef4444', '#64748b', '#06b6d4', '#84cc16', '#a855f7'] }] },
        options: { responsive: true, maintainAspectRatio: false, legend: { position: 'bottom' }, onClick: (e) => this.chartClick(e, 'grupo') }
      })
    },
    chartClick(event, tipo) {
      const chart = tipo === 'faixa' ? this.charts.faixas : this.charts.grupos
      const hit = chart.getElementAtEvent(event)[0]
      if (!hit) return
      const label = chart.data.labels[hit._index]
      if (tipo === 'faixa') this.filtrar('faixa', label, `Faixa: ${label}`)
      if (tipo === 'grupo') this.filtrar('grupo', label, `Grupo: ${label}`)
    },
    destruirCharts() {
      Object.keys(this.charts).forEach(k => { if (this.charts[k]) { this.charts[k].destroy(); this.charts[k] = null } })
    },
    filtrar(tipo, valor, label) {
      this.filtro = { tipo, valor, label }
      this.currentPage = 1
      this.selecionados = []
    },
    aplicarAtalho(filter) {
      if (!filter) return this.limparFiltro()
      this.filtrar(filter.tipo, filter.valor, filter.label)
    },
    limparFiltro() {
      this.filtro = { tipo: null, valor: null, label: 'Todos os títulos em aberto' }
      this.currentPage = 1
    },
    matchFiltro(t) {
      if (!this.filtro.tipo) return true
      if (this.filtro.tipo === 'fornecedor') return Number(t.COD_FORNEC) === Number(this.filtro.valor)
      if (this.filtro.tipo === 'grupo') return t.NOME_GRUPO === this.filtro.valor
      if (this.filtro.tipo === 'faixa') return t.FAIXA === this.filtro.valor
      
      const hoje = Tools.getDataAtual()
      const venc = Tools.formatarDataParaInput(t.DT_VENC)
      
      if (this.filtro.tipo === 'status' && this.filtro.valor === 'atrasado') return Number(t.DIAS_ATRASO) > 0
      if (this.filtro.tipo === 'status' && this.filtro.valor === 'hoje') return venc === hoje
      if (this.filtro.tipo === 'status' && this.filtro.valor === '7dias') {
        const limite = Tools.getAcrescentarData(hoje, 7, 'dias')
        return venc > hoje && venc <= limite
      }
      return true
    },
    
    // --- METODOS BAIXAR INDIVIDUAL (modal-baixar) ---
    async abrirModalBaixar(selecao) {
      this.overlayBaixando = false
      this.baixando = false
      this.$store.commit("areceber/SET_REGISTRO_SELECIONADO_ID", selecao.SEQUENCIA)
      const permissaoPayLoad = {
        login: window.localStorage.getItem('login'),
        permissao: 'AcPropBaixarReceber'
      }
      try {
        this.$store.commit('controle/setAguardando', true)
        const permissaoResult = await this.$http.post(`api/protected/seguranca/validarPermissao`, permissaoPayLoad)
        const permissao = permissaoResult?.data?.permissao ?? null
      
        if (!permissao || permissao == false) {
          this.modalRecebimentos.parametros.permissao = false
          Tools.exibirMensagem(this.$store, 'Permissão', 'warning', 'Usuário sem permissão para baixar recebimentos')
          return
        }

        try {
          await this.carregarContas()
          await this.carregarBancosPix()
        } catch (err) {
          Tools.exibirMensagem(this.$store, 'Operação Indisponível', 'danger', err)
          return
        }

        this.modalRecebimentos.parametros.permissao = true

        this.recebido.areceberId = selecao.SEQUENCIA
        this.recebido.documento = selecao.DOCUMENTO
        this.recebido.parcela = selecao.PARCELAS
        this.recebido.clienteId = selecao.COD_FORNEC
        this.recebido.nomeCliente = selecao.NOME_FORNECEDOR
        this.recebido.dataVencimento = moment(String(selecao.DT_VENC)).format('DD/MM/YYYY')
        this.recebido.dataMovimento = moment(new Date()).format('YYYY-MM-DD')
        this.recebido.tipoRecebimento = '0'

        let ajusteTotal = 0.00
        try {
          const { data } = await this.$http.get(`/api/protected/areceberAjuste/getTotalByAreceberId/${selecao.SEQUENCIA}`)
          ajusteTotal = Number(data?.total) || 0.00
        } catch (err) {
          console.error('Erro ao buscar total de ajustes:', err)
        }

        this.recebido.valorRecebido = 0.00
        this.recebido.valorAjustes = ajusteTotal
        this.recebido.valorAberto = Number(selecao.VL_RESTANTE) || 0.00
        this.recebido.valorDocumento = Number(selecao.VL_DOC) || 0.00
        this.recebido.valorRecebimentos = Tools.arredondarValor((Number(selecao.VL_DOC) || 0.00) + (Number(this.recebido.valorAjustes) || 0.00) - (Number(selecao.VL_RESTANTE) || 0.00))
        this.recebido.valorRestante = 0.00
        this.recebido.vendedorId = selecao.COD_VENDEDOR
        this.recebido.dataBaixa = selecao.DT_BAIXA
        this.recebido.tipoChavePix = null
        this.recebido.nomePagadorPix = null
        this.recebido.bancoPixId = null
        
        this.calcularRecebimento()
        this.$refs['modal-baixar'].show()
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },
    focusModalReceber() {
      if (this.$refs.valorRecebido && this.$refs.valorRecebido.$el) {
        this.$refs.valorRecebido.$el.focus()
      }
    },
    async abrirModalEstornoRecebimento(recebimento) {
      if (this.estorno.parametros.overlayBaixando) {
        return
      }

      const permissaoPayLoad = {
        login: window.localStorage.getItem('login'),
        permissao: 'AcPropReporDocRecebido'
      }

      this.estorno.parametros.permissao = false
      this.estorno.parametros.processando = false
      this.estorno.parametros.overlayBaixando = false
      this.estorno.dados.recebidoId = recebimento.SEQUENCIA
      this.estorno.dados.nomeCliente = recebimento.NOME_FORNECEDOR
      this.estorno.dados.parcela = recebimento.PARCELAS
      this.estorno.dados.documento = recebimento.DOCUMENTO
      this.estorno.dados.motivo = ''
      this.estorno.dados.dataRecebimento = recebimento.DT_BAIXA ? moment(String(recebimento.DT_BAIXA)).format('DD/MM/YYYY') : ''
      this.estorno.dados.valorRecebido = Number(recebimento.VL_PAGO) || 0.00
      this.estorno.dados.valorDocumento = Number(recebimento.VL_PAGO) || 0.00
      this.estorno.dados.usuario = recebimento.USUARIO || ''

      try {
        this.$store.commit('controle/setAguardando', true)
        const permissaoResult = await this.$http.post('api/protected/seguranca/validarPermissao', permissaoPayLoad)
        const permissao = permissaoResult?.data?.permissao ?? null

        if (!permissao) {
          Tools.exibirMensagem(this.$store, 'Permissão', 'warning', 'Usuário sem permissão para estornar')
          return
        }

        this.estorno.parametros.permissao = true

        try {
          const { data } = await this.$http.get(`/api/protected/recebido/detalhar/${recebimento.SEQUENCIA}`)
          if (data) {
            this.estorno.dados.valorDocumento = Number(data.VL_DOC ?? this.estorno.dados.valorDocumento) || this.estorno.dados.valorDocumento
            this.estorno.dados.usuario = data.USUARIO || this.estorno.dados.usuario
          }
        } catch (err) {
          console.error('Erro ao detalhar recebimento para estorno:', err)
        }

        this.$refs['modal-estorno'].show()
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Permissão', 'danger', err)
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },
    focusModalEstornar() {
      this.$nextTick(() => {
        this.$refs.motivoEstorno?.focus()
      })
    },
    async estornarRecebimento() {
      if (this.estorno.parametros.overlayBaixando) {
        return
      }

      if (!this.estorno.dados.motivo || this.estorno.dados.motivo.trim() === '') {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Informe o motivo do estorno!')
        return
      }

      const estornarPayLoad = {
        recebidoId: this.estorno.dados.recebidoId,
        documento: this.estorno.dados.documento,
        parcela: this.estorno.dados.parcela,
        login: window.localStorage.getItem('login'),
        motivo: this.estorno.dados.motivo
      }

      try {
        this.estorno.parametros.processando = true
        this.estorno.parametros.overlayBaixando = true
        this.$store.commit('controle/setAguardando', true)
        await this.$http.post('/api/protected/recebido/estornar', estornarPayLoad)
        this.$refs['modal-estorno'].hide()
        await this.carregar()
        Tools.exibirMensagem(this.$store, 'Estornar', 'success', 'Documento estornado com sucesso!')
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Estornar', 'danger', err)
      } finally {
        this.estorno.parametros.processando = false
        this.estorno.parametros.overlayBaixando = false
        this.$store.commit('controle/setAguardando', false)
      }
    },
    usarRestante() {
      this.recebido.valorRecebido = this.recebido.valorAberto
    },
    calcularRecebimento() {
      this.recebido.valorRestante = Tools.arredondarValor(
        (Number(this.recebido.valorDocumento) || 0.00)
        - (Number(this.recebido.valorRecebimentos) || 0.00)
        - (Number(this.recebido.valorRecebido) || 0.00)
        + (Number(this.recebido.valorAjustes) || 0.00)
      )
    },
    async baixar() {
      if (this.baixando) {
        return
      }

      if (this.validouCamposGeralBaixa == false) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Preencha todos os campos obrigatórios!!!')    
        return
      }

      if (this.recebido.tipoRecebimento === '6') {
        if (!this.recebido.tipoChavePix) {
          Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Informe o tipo de chave PIX!')    
          return
        }
      }

      let movimento = {
        contaId: this.recebido.contaId,
        dtMov: moment(this.recebido.dataMovimento).format('MM-DD-YYYY'),
        login: window.localStorage.getItem('login')
      }
      
      try {
        this.baixando = true
        this.overlayBaixando = true
        this.$store.commit('controle/setAguardando', true)
        
        try {
          await this.$http.post(`/api/protected/caixa/abrirmovimento/`, movimento)
        } catch (error) {
          Tools.exibirMensagem(this.$store, 'Operação Indisponível', 'danger', error)
          return
        }
        
        const documentoPayload = {
          ...this.recebido,
          valorMulta: 0.00,
          valorDesconto: 0.00,
          motivoMulta: null,
          motivoDesconto: null
        }
        
        let pacote = { documento: documentoPayload }
        try {
          await this.$http.post('api/protected/areceberbaixa/baixar', pacote)
        } catch (err) {
          Tools.exibirMensagem(this.$store, 'Operação Indisponível', 'danger', `Falha. Não foi possível concluir a Baixa do Título ${this.recebido.documento}`)
          return
        }
        
        Tools.exibirMensagem(this.$store, 'Aviso', 'success', 'Documento Baixado com Sucesso!!!')
        await this.carregar()
        this.$refs['modal-baixar'].hide()
      } finally {
        this.baixando = false
        this.overlayBaixando = false
        this.$store.commit('controle/setAguardando', false)
      }
    },
    async carregarContas() {
      this.contasOpcoes = []
      this.recebido.contaId = null
      try {
        const response = await this.$http.get(this.$http.defaults.baseURL.concat('api/protected/conta/listar'))
        if (response && response.data) {
          for (let conta of response.data) {
            this.contasOpcoes.push({ value: conta.ID, text: conta.CODIGONOME })
          }
        }
        const contaPadraoResp = await this.$http.get(this.$http.defaults.baseURL.concat('api/protected/parametro/getcontapadrao'))
        if (contaPadraoResp && contaPadraoResp.data && contaPadraoResp.data[0]) {
          this.recebido.contaId = contaPadraoResp.data[0].COD_CONTA
        }
      } catch (error) {
        console.error('Erro em carregarContas:', error)
      }
    },

    // --- METODOS BAIXAR EM LOTE (modal-baixar-lote) ---
    abrirModalBaixarLote() {
      if (this.selecionados.length === 0) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Selecione os títulos na tabela abaixo para realizar a baixa em lote.')
        return
      }
      this.baixarSelecionados()
    },
    focusModalBaixarLote() {
      // Focar se necessário
    },
    somarSelecionados() {
      this.lote.valorTotalRecebido = this.lote.titulos.reduce((acc, curr) => acc + (parseFloat(curr.VL_RESTANTE) || 0), 0)
    },
    async confirmarBaixaLote() {
      const selecionados = this.lote.titulos
      
      if (selecionados.length === 0) {
        Tools.exibirMensagem(this.$store, 'Atenção', 'warning', 'Selecione pelo menos um título para baixar.')
        return
      }
      
      if (!this.lote.contaId) {
        Tools.exibirMensagem(this.$store, 'Atenção', 'warning', 'Selecione a Conta de Destino.')
        return
      }

      if (this.lote.tipoRecebimento === '6') {
        if (!this.lote.tipoChavePix) {
          Tools.exibirMensagem(this.$store, 'Atenção', 'warning', 'Informe o tipo de chave PIX!')
          return
        }
      }

      // Abre/Verifica movimento do caixa
      let movimento = {
        contaId: this.lote.contaId,
        dtMov: moment(this.lote.dataMovimento).format('MM-DD-YYYY'),
        login: window.localStorage.getItem('login')
      }
      
      try {
        this.lote.overlay = true
        
        try {
          await this.$http.post(`/api/protected/caixa/abrirmovimento/`, movimento)
        } catch (error) {
          Tools.exibirMensagem(this.$store, 'Operação Indisponível', 'danger', error)
          return
        }

        const payload = {
          usuario: window.localStorage.getItem('login'), 
          contaId: this.lote.contaId,
          dataMovimento: this.lote.dataMovimento,
          documentos: selecionados.map(t => ({
            areceberId: t.SEQUENCIA, 
            valorRecebido: t.VL_RESTANTE,
            valorDesconto: 0.00,
            valorMulta: 0.00,
            motivoMulta: "",
            motivoDesconto: "",
            tipoRecebimento: this.lote.tipoRecebimento,
            tipoChavePix: this.lote.tipoRecebimento === '6' ? this.lote.tipoChavePix : null,
            nomePagadorPix: this.lote.tipoRecebimento === '6' ? this.lote.nomePagadorPix : null,
            bancoPixId: this.lote.tipoRecebimento === '6' ? this.lote.bancoPixId : null
          })),
          adiantamentos: []
        }

        const response = await this.$http.post('/api/protected/areceberBaixa/baixarLote', payload)
        
        if (response.data && response.data.sucesso === false) {
          throw new Error(response.data.erro || 'Erro ao processar baixa.')
        }
         
        Tools.exibirMensagem(this.$store, 'Sucesso', 'success', 'Baixa em lote realizada com sucesso!')
        this.$refs['modal-baixar-lote'].hide()
        this.selecionados = []
        await this.carregar() 
      } catch (error) {
        console.error(error)
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', error.response?.data?.erro || error.message || 'Erro ao realizar baixa em lote.')
      } finally {
        this.lote.overlay = false
      }
    },

    // --- METODOS GERAR/VISUALIZAR BOLETO ---
    async abrirModalGerarBoleto(item) {
      this.boleto.areceberId    = item.SEQUENCIA
      this.boleto.documento     = item.DOCUMENTO
      this.boleto.parcela       = item.PARCELAS
      this.boleto.clienteId     = item.COD_FORNEC
      this.boleto.empresaId     = 1
      this.boleto.nomeCliente   = item.NOME_FORNECEDOR
      this.boleto.contaId       = null
      this.boleto.valorRecebido = 0.00
      this.boleto.valorMulta    = 0.00
      this.boleto.valorDesconto = 0.00
      this.boleto.valorAberto   = item.VL_RESTANTE
      this.boleto.valorDocumento  = item.VL_DOC
      this.boleto.valorRestante   = item.VL_RESTANTE
      
      this.$store.commit("areceber/SET_REGISTRO_SELECIONADO_ID", item.SEQUENCIA)
      try {
        this.$store.commit('controle/setAguardando', true)
        try {
          await this.carregarEmpresas()
          await this.carregarContas()
        } catch (err) {
          console.log(err)
          Tools.exibirMensagem(this.$store, 'Operação Indisponível', 'danger', err)
          return
        }
        this.$refs['modal-boleto-gerar'].show()
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },
    async gerarBoleto() {
      if (!this.boleto.contaId || !this.boleto.empresaId) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', 'Informe os dados Obrigatórios.')
        return
      }

      const bodyBoleto = {
        areceberIds: [{ARECEBER_ID: this.boleto.areceberId}],
        bancoContaId: this.boleto.contaId,
        memoObservacoesBoleto: '',
      }

      try {
        this.$store.commit('controle/setAguardando', true)
        let resultado
        try {
          resultado = await this.$http.post('/api/protected/boleto/gerar', bodyBoleto)
        } catch (err) {
          Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
          console.error('Erro ao gerar boleto:', err)
          return
        }

        const boletoGerado = (resultado?.data) ?? null
        if (!boletoGerado || !boletoGerado.boletosIds || boletoGerado.boletosIds.length === 0) {
          Tools.exibirMensagem(this.$store, 'Boleto', 'warning', 'Não foi possível gerar o Boleto!')
          return
        }

        const bodyGerar = {
          contaId: this.boleto.contaId,
          boletoId: boletoGerado.boletosIds[0]
        }

        try {
          resultado = await this.$http.post('/api/protected/integrador/gerarBoleto', bodyGerar)
          const boletoPDF = (resultado?.data) ?? null
          if (!boletoPDF || !boletoPDF.sucesso || !boletoPDF.pdfBase64) {
            const erro = resultado?.erro || 'Não foi possível baixar o PDF do Boleto.'
            Tools.exibirMensagem(this.$store, 'Erro', 'danger', erro)
            return
          }

          const byteCharacters = atob(boletoPDF.pdfBase64)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: 'application/pdf' })
          const url = window.URL.createObjectURL(blob)
          window.open(url)
        } catch (err) {
          Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
          console.error('Erro ao gerar boleto PDF:', err)
        }

        await this.carregar()
        this.$refs['modal-boleto-gerar'].hide()
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },
    async cancelarBoleto(item) {
      this.$store.commit("areceber/SET_REGISTRO_SELECIONADO_ID", item.SEQUENCIA)

      const resposta = await this.$bvModal.msgBoxConfirm(`Cancelar Boleto: ${item.BOLETO_ID} Do Cliente: ${item.NOME_FORNECEDOR} ?`, {
        title: 'Cancelar Boleto?',
        size: 'lg',
        buttonSize: 'lg',
        okVariant: 'primary',
        okTitle: 'SIM',
        cancelTitle: 'NAO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      })

      if (resposta == false) {
        return
      }

      const bodyBoleto = {
        areceberId: item.SEQUENCIA,
        login: window.localStorage.getItem('login'),
      }

      try {
        this.$store.commit('controle/setAguardando', true)
        let resultado
        try {
          resultado = await this.$http.post('/api/protected/boleto/cancelar', bodyBoleto)
        } catch (err) {
          Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
          console.error('Erro ao cancelar boleto:', err)
          return
        }

        resultado = (resultado?.data) ?? null
        if (!resultado || !resultado.sucesso) {
          Tools.exibirMensagem(this.$store, 'Erro', 'danger', resultado?.erro || 'Erro desconhecido')
        } else {
          Tools.exibirMensagem(this.$store, 'Sucesso', 'success', 'Boleto cancelado com sucesso!')
        }

        await this.carregar()
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },
    async carregarEmpresas() {
      try {
        this.empresas.items = []
        this.empresas.options = []
        const response = await this.$http.get(this.$http.defaults.baseURL.concat('api/protected/empresa/getTodas'))
        if (response && response.data) {
          this.empresas.items = response.data
          for (let empresa of response.data) {
            this.empresas.options.push({ value: empresa.CODIGO, text: empresa.RAZAOSOCIAL })
          }
          if (response.data.length > 0) {
            this.boleto.empresaId = response.data[0].CODIGO
          }
        }
      } catch (err) {
        console.error('Erro ao carregar empresas:', err)
      }
    },
    classIconeBoleto(boleto) {
      let valor = boleto?.trim()
      switch (valor) {
        case '0': return ''
        case '1': return ''
        case '2': return 'fa fa-suitcase text-warning'
        case '3': return 'fa fa-lock text-success'
        case '4': return 'fa fa-minus-circle text-danger'
        case '5': return 'fa fa-exclamation-circle text-info'
        default: return ''
      }
    },

    // --- FILTRAGEM E ATUALIZACAO ---
    async baixarSelecionados() {
      if (this.selecionados.length === 0) return;
      if (this.selecionados.length === 1) {
        return this.abrirModalBaixar(this.selecionados[0])
      }
      
      const firstClient = this.selecionados[0].COD_FORNEC
      const sameClient = this.selecionados.every(t => t.COD_FORNEC === firstClient)
      
      if (sameClient) {
        this.lote.clienteId = firstClient
        this.lote.nomeCliente = this.selecionados[0].NOME_FORNECEDOR
        this.lote.contaId = null
        this.lote.tipoRecebimento = '0'
        this.lote.dataMovimento = moment().format('YYYY-MM-DD')
        this.lote.todosSelecionados = true
        this.lote.tipoChavePix = null
        this.lote.nomePagadorPix = null
        this.lote.bancoPixId = null
        
        // Mapeia títulos selecionados na mesa para o lote
        this.lote.titulos = this.selecionados.map(t => ({
          ...t,
          selecionado: true
        }))
        
        this.somarSelecionados()
        
        await this.carregarContas()
        
        this.$refs['modal-baixar-lote'].show()
      } else {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Para baixar em lote, todos os títulos selecionados devem pertencer ao mesmo cliente.')
      }
    },

    isCardSelected(card) {
      if (!card.filter) {
        return !this.filtro.tipo
      }
      return this.filtro.tipo === card.filter.tipo && this.filtro.valor === card.filter.valor
    },
    editar(titulo) {
      this.$router.push(`/movimentos/contasreceber/edit/${titulo.SEQUENCIA}`)
    },
    percent(v, max) { return Math.max(3, Math.min(100, ((Number(v) || 0) / (Number(max) || 1)) * 100)) },
    formatBRL(v) { return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
    formatDate(v) { return v ? moment(v).format('DD/MM/YYYY') : '-' },
    formatCompact(v) { const n = Number(v) || 0; return n >= 1000000 ? `${(n / 1000000).toFixed(1)} mi` : n >= 1000 ? `${(n / 1000).toFixed(0)} mil` : n.toFixed(0) }
  }
}
</script>

<style scoped>
.areceber-workbench { min-height: calc(100vh - 110px); background: #eef2f7; padding: 18px; }
.workbench-header { display: flex; justify-content: space-between; gap: 16px; align-items: center; padding: 18px 20px; background: linear-gradient(135deg, #0f172a, #1e3a8a 58%, #1d4ed8); color: #fff; border-radius: 8px; box-shadow: 0 12px 26px rgba(15,23,42,.18); }
.workbench-header > div:first-child { display: flex; align-items: center; gap: 12px; }
.workbench-header h3 { margin: 0; font-weight: 800; text-transform: uppercase; }
.workbench-header span { color: rgba(255,255,255,.72); }
.back-btn { color: #fff; font-size: 1.25rem; }
.header-actions { display: flex; gap: 10px; align-items: center; min-width: 460px; }
.header-actions .form-control { border: 0; border-radius: 999px; }
.kpi-grid { display: grid; grid-template-columns: repeat(5, minmax(150px, 1fr)); gap: 14px; margin: 16px 0; }
.kpi-card { position: relative; min-height: 118px; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; padding: 15px; text-align: left; box-shadow: 0 8px 18px rgba(15,23,42,.06); cursor: pointer; }
.kpi-card small, .kpi-card span { display: block; color: #64748b; font-weight: 700; }
.kpi-card small { text-transform: uppercase; font-size: .68rem; }
.kpi-card strong { display: block; color: #0f172a; font-size: 1.15rem; margin-top: 8px; }
.kpi-card i { position: absolute; right: 14px; bottom: 12px; font-size: 1.6rem; opacity: .18; }
.kpi-card.blue { border-top: 3px solid #2563eb; } .kpi-card.red { border-top: 3px solid #ef4444; } .kpi-card.amber { border-top: 3px solid #f59e0b; } .kpi-card.green { border-top: 3px solid #10b981; } .kpi-card.teal { border-top: 3px solid #0f766e; }

/* Dashboard alignment and card heights */
.workbench-grid { display: grid; grid-template-columns: 1.5fr 1fr 1fr 1fr; gap: 14px; }
.panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px; box-shadow: 0 10px 22px rgba(15,23,42,.06); height: 350px; display: flex; flex-direction: column; }
.panel-wide { grid-row: auto; }
.panel-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding-bottom: 10px; margin-bottom: 10px; border-bottom: 1px solid #edf2f7; }
.panel-head strong { display: block; color: #111827; font-size: .95rem; }
.panel-head span { display: block; color: #64748b; font-size: .76rem; }

.supplier-board { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; flex: 1; overflow-y: auto; padding-right: 4px; }
.supplier-card { border: 1px solid #e2e8f0; background: #f8fafc; border-radius: 8px; padding: 12px; text-align: left; cursor: pointer; }
.supplier-card.active, .supplier-card:hover { border-color: #0891b2; background: #ecfeff; }
.supplier-card span, .supplier-card strong, .supplier-card em { display: block; }
.supplier-card span { color: #0f172a; font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.supplier-card strong { margin: 6px 0 2px; }
.supplier-card em { color: #64748b; font-style: normal; font-size: .74rem; margin-bottom: 8px; }

.chart-wrap { height: 260px; }
.today-paid strong { display: block; color: #0f766e; font-size: 1.8rem; }
.today-paid span { color: #64748b; }
.recent-list { flex: 1; overflow-y: auto; margin-top: 12px; }
.recent-row { display: flex; justify-content: space-between; gap: 8px; border-top: 1px solid #f1f5f9; padding: 8px 0; }
.recent-row strong, .recent-row span { display: block; }
.recent-row strong { color: #0f172a; font-size: .78rem; }
.recent-row span { color: #64748b; font-size: .72rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 170px; }
.recent-row em { color: #0f766e; font-style: normal; font-weight: 800; }
.recent-row-actions { display: flex; align-items: center; gap: 8px; }

.table-panel { margin-top: 14px; height: auto; }

/* Estilos para KPI cards ativos/selecionados */
.kpi-card.active {
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-2px);
  border-width: 2px !important;
}
.kpi-card.blue.active { background: #eff6ff !important; border-color: #2563eb !important; }
.kpi-card.red.active { background: #fef2f2 !important; border-color: #ef4444 !important; }
.kpi-card.amber.active { background: #fffbeb !important; border-color: #f59e0b !important; }
.kpi-card.green.active { background: #f0fdf4 !important; border-color: #10b981 !important; }
.kpi-card.teal.active { background: #f0fdfa !important; border-color: #0f766e !important; }

/* Premium Modal styling classes to match AreceberListagem */
.modal-header-premium {
  color: #fff;
  border-bottom: 0;
}
.header-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  font-size: 1.3rem;
}
.bg-white-10 {
  background: rgba(255, 255, 255, 0.15);
}
.modal-info-bar {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
}
.modal-label-premium {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 4px;
}
.modal-section-title {
  font-size: 0.88rem;
  font-weight: 800;
  color: #1e293b;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 4px;
  text-transform: uppercase;
}
.gap-1 {
  gap: 0.25rem;
}
.gap-2 {
  gap: 0.5rem;
}
</style>



// WEBPACK FOOTER //
// src/views/movimentos/contasreceber/AreceberAnalyticsView.vue